import { useContext } from 'react'

import { TopSectionPropsContext } from '../context/TopSectionPropsContext'

/**
 * Hook to access TopSection server props and derived flags.
 * Must be used within a TopSectionPropsProvider.
 *
 * @returns {import('../context/TopSectionPropsContext').TopSectionPropsContextValue}
 * @throws {Error} If used outside TopSectionPropsProvider
 *
 * @example
 * function MyComponent() {
 *   const { is3dPrintModel, role, currentUsername } = useTopSectionProps()
 *   // ...
 * }
 */
export function useTopSectionProps() {
  const context = useContext(TopSectionPropsContext)

  if (context === null) {
    throw new Error(
      'useTopSectionProps must be used within a TopSectionPropsProvider. ' +
      'Ensure the component is rendered inside <TopSectionPropsProvider>.'
    )
  }

  return context
}

/**
 * Convenience hook that returns only the is3dPrintModel flag.
 * Must be used within a TopSectionPropsProvider.
 *
 * @returns {boolean} Whether the item is a 3D print model
 * @throws {Error} If used outside TopSectionPropsProvider
 *
 * @example
 * function MyComponent() {
 *   const is3dPrintModel = useIs3dPrintModel()
 *   // ...
 * }
 */
export function useIs3dPrintModel() {
  const { is3dPrintModel } = useTopSectionProps()

  return is3dPrintModel
}

/**
 * Convenience hook that returns only the role string.
 * Must be used within a TopSectionPropsProvider.
 *
 * @returns {string} The current user's role (guest, user, buyer, seller)
 * @throws {Error} If used outside TopSectionPropsProvider
 */
export function useRole() {
  const { role } = useTopSectionProps()

  return role
}
